import React, {useCallback, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    selectLoadingNewColour,
    selectMaterial,
    selectedMenuSet,
} from 'components/customer/AdvancedMaterials/store/materialSlice';
import {shallowEqual} from 'react-redux';
import styled from 'styled-components';
import {
    Title,
    TitleContainer,
} from 'components/customer/AdvancedMaterials/Doors/MaterialType';
import {
    ImageContainer,
    MaterialImage,
    SubContainer,
    Name as MaterialName,
} from 'components/customer/AdvancedMaterials/Materials/Material';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {NewSelectionPopover} from 'components/customer/AdvancedMaterials/CurrentSelections/NewSelectionPopover';
import {Icon} from 'shared/helpers';
import {useSelectedMenu} from 'components/customer/AdvancedMaterials/helpers/useSelectedMenu';
import {Menu} from 'components/customer/AdvancedMaterials/entity/Menu';
import {mobile} from 'shared/Breakpoints';
import {isMobile, isTablet} from 'react-device-detect';
import {useAppContext} from 'contexts';
export const BenchtopSelection = () => {
    const dispatch = useAppDispatch();

    const material = useAppSelector(selectMaterial, shallowEqual);
    const loadingNewColour = useAppSelector(selectLoadingNewColour);

    const onClickMaterial = useCallback(() => {
        dispatch(selectedMenuSet(Menu.MATERIAL, true));
    }, [dispatch]);
    const {selectedMenu} = useSelectedMenu();
    const [materialZoomed, setMaterialZoomed] = useState(false);
    const zoomAllowed = !Boolean(isMobile || isTablet);
    const {userProfile} = useAppContext();
    const hideSubstrateName = userProfile?.hideSubstrateName;

    const handleMaterialZoom = useCallback(
        (value?: boolean) => {
            if (!zoomAllowed) return;

            if (typeof value === 'boolean') {
                setMaterialZoomed(value);
            } else {
                setMaterialZoomed((prev) => !prev);
            }
        },
        [zoomAllowed]
    );

    const handleMouseLeaveMaterial = useCallback(() => {
        handleMaterialZoom(false);
    }, [handleMaterialZoom]);

    const handleZoomClickMaterial = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleMaterialZoom();
        },
        [handleMaterialZoom]
    );

    return (
        <>
            <LocalTitleContainer>
                <Title>Current Selection</Title>
            </LocalTitleContainer>
            <Container>
                <LocalSubContainer
                    onClick={onClickMaterial}
                    onMouseLeave={handleMouseLeaveMaterial}
                    $selected={selectedMenu === Menu.MATERIAL}
                    $dropdownOpen={false}
                    $previewOnly={true}
                    $zoomed={materialZoomed}>
                    <Label $selected={selectedMenu === Menu.MATERIAL}>
                        <span>Material</span>
                        {zoomAllowed && material ? (
                            <ZoomIcon onClick={handleZoomClickMaterial}>
                                <Icon iconName="zoom.svg" title="Zoom in" />
                            </ZoomIcon>
                        ) : null}
                    </Label>

                    {materialZoomed ? (
                        <NewSelectionPopover
                            selected={selectedMenu == Menu.MATERIAL}
                            image={material.image}
                            title="Material"
                            description={
                                (material?.data as {displayName?: string})
                                    ?.displayName ?? ''
                            }
                            show={materialZoomed}
                        />
                    ) : (
                        <div style={{maxWidth: '200px'}}>
                            <ImageContainer>
                                <MaterialImage src={material.image} />
                            </ImageContainer>
                            <MaterialName>
                                <span>
                                    <strong>{material.thickness}mm</strong>{' '}
                                    {parseHtmlString(material.name)}{' '}
                                    <em>{material.finish.name}</em>
                                    {hideSubstrateName
                                        ? null
                                        : ` ${material.substrate}`}
                                </span>
                            </MaterialName>
                            {loadingNewColour ? (
                                <LocalIcon iconName="tail-spin.svg" />
                            ) : null}
                        </div>
                    )}
                </LocalSubContainer>
            </Container>
        </>
    );
};

const LocalIcon = styled((props) => <Icon {...props} />)`
    width: 25px !important;
    position: absolute;
    bottom: 3px;
    right: 5px;
`;

const Container = styled.section`
    display: flex;
    margin: 5px 0;
    gap: 5px;
`;

const LocalSubContainer = styled(SubContainer)<{
    $zoomed: boolean;
}>`
    position: relative;
    transition:
        transform 0.3s ease,
        max-width 0.3s ease;

    > div > img {
        max-width: 100% !important;
        height: ${({$zoomed}) => ($zoomed ? 'auto' : '100px')};
        transition: height 0.3s ease; // Only transition height
    }

    min-height: 180px;
    ${mobile`
        transform: translateX(0) !important;

        > div > img {
            max-width: 100% !important;
            height: auto !important;
        }
    `}
`;

const Label = styled.div<{$selected: boolean}>`
    position: absolute;
    top: 0;
    left: 0;
    padding: 2px 10px;
    color: white;
    font-weight: bold;
    border-top-left-radius: 5px;
    border-bottom-right-radius: 5px;
    background: rgb(var(--secondary_colour));
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;

    ${({$selected}) =>
        $selected &&
        `
    background: rgb(var(--primary_colour));
  `}
`;

const ZoomIcon = styled.div`
    pointer-events: auto;
    cursor: pointer;

    > img {
        width: 25px;
    }
`;

const LocalTitleContainer = styled(TitleContainer)`
    margin: 0;
`;
